export interface IOnuOption {
  value: string;
  label: string;
}

export interface IOnuViewEthernetPort {
  id: string;
  port: string;
  adminState: string;
  mode: string;
  dhcp: string;
  vlanId: string;
  vlanDhcp: string;
  portMode: string;
  onuMode: string;
  state: string;
  extraVlans: string;
}

export interface IOnuViewWifiPort {
  id: string;
  port: string;
  band: string;
  adminState: string;
  mode: string;
  ssid: string;
  dhcp: string;
  vlanId: string;
  vlanDhcp: string;
  portMode: string;
  onuMode: string;
  state: string;
  extraVlans: string;
  authMode: string;
  tr069Slot: string;
}

export interface IOnuViewWifi {
  /** el equipo tiene WiFi (hay interruptor para habilitarlo) */
  supported: boolean;
  enabled: boolean;
  ports: IOnuViewWifiPort[];
  available: IOnuViewWifiPort[];
}

/** Estado de enlace de un puerto Ethernet (api/onu/ethernet_port_link). */
export interface IOnuPortLink {
  supported: boolean;
  link: string;
  speed_mbps: number | null;
  duplex: string | null;
  port_type: string;
}

export interface IOnuStatusSignal {
  onu_status: string;
  last_status_change: string;
  last_status_change_unix: number | null;
  last_status_down_cause: string | null;
  server_time_unix: number | null;
  onu_signal: string;
  onu_signal_value: string;
  onu_signal_rx1310: string | null;
  onu_signal_rx1490: string | null;
  signal_limit_warning: number;
  signal_limit_critical: number;
  onu_tx_power: string | null;
  olt_tx_power: string | null;
  downstream_loss_db: string | null;
  upstream_loss_db: string | null;
  distance: string;
  /** la OLT no respondió la lectura óptica */
  olt_read_skipped?: boolean;
}

/** Pantalla onu/view de SmartOLT, tal como la devuelve el backend. */
export interface IOnuView {
  onuId: number;
  adminDisabled: boolean;
  disconnections: string;
  hasWifi: boolean;
  alerts: { type: string; message: string }[];
  fields: Record<string, string>;
  olt: { id: string; name: string };
  board: string;
  port: { value: string; label: string };
  onu: { name: string; currentOnuId: string; channelType: string };
  gponType: { value: string; label: string };
  sn: string;
  onuType: { id: string; name: string; customTemplateId: string };
  location: {
    zoneId: string;
    zoneName: string;
    odbId: string;
    odbName: string;
    odbPort: string;
    sectorId: string;
    sectorName: string;
    name: string;
    description: string;
    contact: string;
    latitude: string;
    longitude: string;
  };
  authorizationDate: string;
  externalId: string;
  imageUrl: string;
  tr069Profile: string;
  mgmtIp: Record<string, string>;
  attachedVlans: string;
  mainVlanId: string;
  /** ids internos de SmartOLT de las VLANs adjuntas (incluida la principal) */
  attachedVlanIds: string[];
  mainVlanDbId: string;
  /** máximo de VLANs por ONU; null = sin límite */
  maxVlans: number | null;
  mode: Record<string, string>;
  modeLabel: string;
  wanSetupMode: string;
  speedProfiles: {
    download: string;
    upload: string;
    downloadId: string;
    uploadId: string;
    /** solo en las OLT con service-port (C600); el resto los trae vacíos */
    servicePort?: string;
    useSvlan?: string;
    svlanId?: string;
    useOtherAllTlsVlan?: string;
  } | null;
  ethernetPorts: IOnuViewEthernetPort[];
  voip: { label: string; mode: string };
  catv: string;
  /** opciones de los selects de la página; SmartOLT puede no traer alguno */
  options: Partial<Record<string, IOnuOption[]>>;
  statusSignal: IOnuStatusSignal | null;
}

export interface IOnuHistoryItem {
  action: string;
  user: string;
  ip: string;
  date: string;
}

/** Nota de la ONU en SmartOLT (onu/notes_page). */
export interface IOnuNote {
  id: number;
  note: string;
  user_name: string;
  created_at_label: string;
  is_ticket: boolean;
  assigned_user_id: number;
  assignee_name: string;
  is_resolved: boolean;
  subject: string;
  status_label: string;
  resolver_name: string;
  resolved_at_label: string;
  replies: { note?: string; user_name?: string; created_at_label?: string }[];
  can_delete: boolean;
  can_assign: boolean;
  can_reply: boolean;
  can_resolve: boolean;
  can_reassign: boolean;
  can_reopen: boolean;
}

/** Respuesta típica de las rutas api/* de SmartOLT. */
export interface ISmartOltList<T> {
  response: T[];
  status: boolean;
}

export type OnuReadResource =
  | 'status_signal'
  | 'status_optics'
  | 'status'
  | 'running_config'
  | 'sw_info'
  | 'traffic'
  | 'signal'
  | 'status_history'
  | 'history'
  | 'traffic_usage'
  | 'port_link'
  | 'wifi'
  | 'wifi_password'
  | 'notes'
  | 'vlans_ethernet'
  | 'vlans_default'
  | 'vlans_extra'
  | 'vlans_mgmt'
  | 'mgmt_ips'
  | 'zones'
  | 'odbs'
  | 'odb_ports'
  | 'sectors'
  | 'onu_types'
  | 'olts'
  | 'olt_boards'
  | 'olt_ports'
  | 'available_onu_ids'
  | 'voip_profiles'
  | 'odb_details'
  | 'odb_search_onus'
  | 'odb_status_series'
  | 'is_duplicate_sn'
  | 'tr069'
  | 'tr069_devices';

export type OnuAction =
  | 'update_external_id'
  | 'update_location'
  | 'change_onu_type'
  | 'update_sn'
  | 'move_onu'
  | 'change_allocated_onu'
  | 'update_gpon_type'
  | 'update_speed_profiles'
  | 'update_mode'
  | 'update_mgmt_ip'
  | 'update_extra_vlans'
  | 'update_port'
  | 'enable_voip'
  | 'disable_voip'
  | 'clear_config_voip'
  | 'wifi_status'
  | 'update_wifi_port'
  | 'firmware_upgrade'
  | 'odb_set_ports'
  | 'odb_attach_onu'
  | 'odb_detach_onu'
  | 'odb_clear_port'
  | 'pon_refresh'
  | 'tr069_save'
  | 'reboot'
  | 'resync'
  | 'restore_defaults'
  | 'enable'
  | 'disable'
  | 'note_add'
  | 'note_reply'
  | 'note_resolve'
  | 'note_reopen'
  | 'note_delete'
  | 'note_assign'
  | 'note_reassign';

/** Un valor del panel TR-069 de SmartOLT (OID, control y si se puede editar aquí). */
export interface ITr069Field {
  label: string;
  oid: string;
  /** valor tal como lo muestra SmartOLT */
  display: string;
  control: 'text' | 'select' | 'radio' | 'none';
  /** valor actual del control: lo que se envía al guardar */
  value: string;
  options: { value: string; label: string }[];
  editable: boolean;
  secret: boolean;
  valid: string | null;
  validMessage: string | null;
  remember: 'wifi' | 'pppoe' | 'webui' | null;
  hint: string;
  associatedOid: string | null;
}

export interface ITr069Group {
  name: string;
  icon: string;
  layout: 'list' | 'table';
  fields: ITr069Field[];
  columns: string[];
  rows: ITr069Field[][];
  empty: string;
}

export interface ITr069Status {
  /** 'current' o true al leer; 'countdown' si el equipo solo informa cada 60 s; 'reload' */
  status: string;
  remaining: number | null;
  message: string | null;
  delay60: boolean;
  groups: ITr069Group[];
}

/** Equipos conectados a un WiFi. */
export interface ITr069Devices {
  ok: boolean;
  message: string | null;
  count: number;
  columns: string[];
  rows: string[][];
}

export type OnuSyncField = 'name' | 'description' | 'contact' | 'coordinates';

/** Un dato del cliente o contrato frente a lo que tiene SmartOLT. */
export interface IOnuSyncRow {
  key: OnuSyncField;
  label: string;
  /** lo que tiene SmartOLT */
  current: string;
  /** lo que propone el sistema, ya adaptado a lo que acepta SmartOLT */
  system: string;
  /** texto original del sistema, para mostrar qué se adaptó */
  original: string;
  adjusted: boolean;
  changed: boolean;
  available: boolean;
}

export interface IOnuSyncPreview {
  contractId: number;
  clientName: string;
  rows: IOnuSyncRow[];
  pending: number;
}

/** Puerto de un splitter: con una ONU, vacío (`free`) o una ONU sin puerto (`unassigned`). */
export interface IOdbPort {
  id: string | null;
  port: number | string | null;
  free: boolean;
  unassigned: boolean;
  name: string | null;
  sn: string | null;
  client_external_id: string | null;
  status: string | null;
  signal: string | null;
  rx_1490: string | null;
  signal_limit_warning: string | null;
  signal_limit_critical: string | null;
  distance: string | null;
  address: string | null;
  zone_name: string | null;
  olt_id: string | null;
  olt_name: string | null;
  board: string | null;
  pon_port: string | null;
  pon: string | null;
  last_status_change: string | null;
  has_coords: number;
  latitude: number | string | null;
  longitude: number | string | null;
}

/** Detalle de un splitter (diagnostics/get_odb_details). */
export interface IOdbDetails {
  id: number;
  name: string;
  external_id: string | null;
  comment: string | null;
  note: string | null;
  nr_of_ports: number;
  first_port_number: number;
  total_ports_defined: boolean;
  used_ports: number;
  signal_min: string | null;
  signal_avg: number | null;
  signal_max: string | null;
  /** [con señal, online, LOS, power fail, offline, señal baja, promedio, mínimo, máximo] */
  health: Array<number | null> | null;
  input_state: unknown;
  ports: IOdbPort[];
}

/** Tramo de estado de una ONU (onu_status_history/get_series_for_onus), en ms. */
export interface IOnuStatusSegment {
  from: number;
  to: number;
  status: string;
}

/** Barra de estado de las últimas 24 h por id de ONU. */
export type IOdbStatusSeries = Record<string, { segments?: IOnuStatusSegment[] }>;

export interface IOdbSearchOnu {
  id: string;
  olt_id: string;
  location_name: string | null;
  client_external_id: string | null;
  sn: string;
  odb_id: string | null;
  odb_port: string | null;
  odb_name: string | null;
  olt_name: string | null;
  odb_first_port_number: string | null;
}
